import _ from 'lodash';

// Reconstruct a vue-cropperjs crop box from stored focal-point processing data.
// Shared by the unified Edit drawer and the legacy ImageCropperModal so the
// geometry is identical everywhere.
export const getImageCrop = (image, aspectRatio) => {
    const crop = {};

    let processing = _.get(image, 'settings.processing');
    if (!processing && image.settings && image.settings.x !== undefined) {
        processing = image.settings;
    }

    const imageWidth = image.width || 400;
    const imageHeight = image.height || 400;

    if (processing && (processing.x !== undefined || processing.y !== undefined)) {
        const fx = parseFloat(processing.x !== undefined ? processing.x : 0.5);
        const fy = parseFloat(processing.y !== undefined ? processing.y : 0.5);
        const z = parseFloat(processing.z || 1) || 1;

        // The saved crop's OWN aspect (its stored output w/h) is authoritative when
        // restoring the box — NOT the live tool ratio (which is "Free" on open and would
        // wrongly stretch a square crop to the image's aspect). Fall back to the passed
        // ratio / image aspect only when w/h aren't stored.
        const storedWidth = parseFloat(processing.w);
        const storedHeight = parseFloat(processing.h);
        const cropAspectRatio = (storedWidth > 0 && storedHeight > 0)
            ? storedWidth / storedHeight
            : (aspectRatio || (imageWidth / imageHeight));
        const originalAspectRatio = imageWidth / imageHeight;

        // EXACT inverse of getProcessingFromCropData / getProcessingObjectFromCropperData:
        // they derive z from the LIMITING dimension (originalAspect vs cropAspect), so the
        // box must be reconstructed with the same branch or its aspect/size won't match.
        if (originalAspectRatio > cropAspectRatio) {
            crop.height = imageHeight / z;
            crop.width = crop.height * cropAspectRatio;
        } else {
            crop.width = imageWidth / z;
            crop.height = crop.width / cropAspectRatio;
        }

        crop.x = (imageWidth * fx) - (crop.width / 2);
        crop.y = (imageHeight * fy) - (crop.height / 2);
    }

    return crop;
};

// Map a crop box between the natural-image space and the FLIPPED space the cropper
// renders. cropper.js keeps the crop box in natural coordinates while scaleX/scaleY
// only mirror the displayed pixels, so to keep the box on the same motive after a flip
// we mirror the box position too. Flips are involutions → one function both directions.
export const applyFlipToBox = (box, flipHorizontal, flipVertical, imageWidth, imageHeight) => {
    let { x, y, width, height } = box;
    if (flipHorizontal) x = imageWidth - (x + width);
    if (flipVertical) y = imageHeight - (y + height);
    return { x, y, width, height };
};

// Map a crop box from natural coords into the coordinate frame cropper.js uses AFTER a
// clockwise rotation, so the box lands on the same motive. r ∈ {0,90,180,270}.
export const rotateBox = (box, rotateDegrees, imageWidth, imageHeight) => {
    const r = ((rotateDegrees % 360) + 360) % 360;
    const { x, y, width, height } = box;
    if (r === 90) return { x: imageHeight - (y + height), y: x, width: height, height: width };
    if (r === 180) return { x: imageWidth - (x + width), y: imageHeight - (y + height), width, height };
    if (r === 270) return { x: y, y: imageWidth - (x + width), width: height, height: width };
    return { x, y, width, height };
};

// Inverse of rotateBox: map a box from the rotated frame back to natural coords.
export const unrotateBox = (box, rotateDegrees, imageWidth, imageHeight) => {
    const r = ((rotateDegrees % 360) + 360) % 360;
    // The inverse of a CW rotation by r is a CW rotation by (360 - r) in the rotated
    // frame, whose dimensions are swapped for 90/270.
    const inverse = (360 - r) % 360;
    const rotatedWidth = (r === 90 || r === 270) ? imageHeight : imageWidth;
    const rotatedHeight = (r === 90 || r === 270) ? imageWidth : imageHeight;
    return rotateBox(box, inverse, rotatedWidth, rotatedHeight);
};

// Rotate a normalized focal point (fractions of the image dims) by r degrees clockwise,
// following the image-content rotation. r ∈ {0,90,180,270}. Used to convert focal params
// between the source frame and the output (rotated) frame.
const rotateFocalPoint = (fx, fy, r) => {
    if (r === 90) return [1 - fy, fx];
    if (r === 180) return [1 - fx, 1 - fy];
    if (r === 270) return [fy, 1 - fx];
    return [fx, fy];
};

// SOURCE-frame focal params (x,y = fractions of the source; w,h = the pre-rotation box) →
// OUTPUT-absolute params (x,y = fractions of the flipped+rotated image; w,h = the FINAL
// output dims). This is the imgix model: the stored geometry always describes the final
// image, so every consumer reading w/h gets the right aspect without knowing about rotate.
// Order matches the bake (ProcessImage rotate-then-crop) and the canvas CSS transform:
// flip first, then rotate.
export const toOutputFrame = (params, rotate, flipH, flipV) => {
    const r = ((Number(rotate) % 360) + 360) % 360;
    let fx = parseFloat(params.x);
    let fy = parseFloat(params.y);
    if (flipH) fx = 1 - fx;
    if (flipV) fy = 1 - fy;
    [fx, fy] = rotateFocalPoint(fx, fy, r);
    const swap = (r === 90 || r === 270);
    return {
        ...params,
        x: fx,
        y: fy,
        w: swap ? params.h : params.w,
        h: swap ? params.w : params.h,
    };
};

// Inverse of toOutputFrame: OUTPUT-absolute params → SOURCE-frame, for restoring the
// canvas crop rect (which lives in source coordinates). Inverse order: un-rotate, un-flip.
export const toSourceFrame = (params, rotate, flipH, flipV) => {
    const r = ((Number(rotate) % 360) + 360) % 360;
    const inverse = (360 - r) % 360;
    const swap = (r === 90 || r === 270);
    let fx = parseFloat(params.x);
    let fy = parseFloat(params.y);
    [fx, fy] = rotateFocalPoint(fx, fy, inverse);
    if (flipH) fx = 1 - fx;
    if (flipV) fy = 1 - fy;
    return {
        ...params,
        x: fx,
        y: fy,
        w: swap ? params.h : params.w,
        h: swap ? params.w : params.h,
    };
};

// Convert vue-cropperjs getData(true) output into focal-point processing params
// (the shape the backend ApplyImageCrop mutation / ImageProcessingService expect).
export const getProcessingFromCropData = (data, originalWidth, originalHeight) => {
    const width = data.width;
    const height = data.height;
    const x = data.x;
    const y = data.y;

    const fx = (x + width / 2) / originalWidth;
    const fy = (y + height / 2) / originalHeight;

    // The backend (ProcessImage::crop) reconstructs the crop region from z + the
    // requested aspect, choosing the LIMITING dimension by comparing the original
    // aspect to the CROP's aspect (not to 1). z must be derived the same way or the
    // rendered region won't match the box the user drew (wrong size/aspect).
    const originalAspectRatio = originalWidth / originalHeight;
    const cropAspectRatio = width / height;
    const fz = originalAspectRatio > cropAspectRatio
        ? originalHeight / height
        : originalWidth / width;

    const result = {
        fit: 'crop',
        crop: 'focalpoint',
        x: fx,
        y: fy,
        z: fz,
        w: Math.round(width),
        h: Math.round(height),
    };
    // Transform tools as imgix query keys (the processing object mirrors the query string):
    // rot (degrees, clockwise) + flip (h|v|hv). cropper.js reports rotation via data.rotate
    // and flips via scaleX/scaleY (-1 = mirrored). Emitted only when set.
    const rot = Math.round(data.rotate || 0);
    if (rot) result.rot = rot;
    const flip = (((data.scaleX || 1) < 0) ? 'h' : '') + (((data.scaleY || 1) < 0) ? 'v' : '');
    if (flip) result.flip = flip;
    return result;
};

// ===== Canvas-expand fill =====
// Expanding the canvas reuses the SAME focal-point crop model — the crop frame simply
// extends beyond the image (focal point outside [0,1] / zoom z < 1), and the revealed area
// is filled. So getProcessingFromCropData / getImageCrop above already cover it; the editor
// only needs to (a) detect when the crop exceeds the image and (b) attach the fill params.

// True when the crop rectangle (source px) extends past the image on any side.
export const cropExceedsImage = (crop, imageWidth, imageHeight) => {
    if (!crop) return false;
    return crop.x < -0.5 || crop.y < -0.5
        || (crop.x + crop.width) > imageWidth + 0.5
        || (crop.y + crop.height) > imageHeight + 0.5;
};

// The fill params attached to a focal-crop processing object when the frame exceeds the
// image. fillMode: 'solid' | 'transparent' | 'ai'. fillColor is a 6-digit hex (no '#').
// imgix fill-color is alpha-FIRST (00 alpha ⇒ transparent).
export const getFillParams = (fillMode, fillColor, fillPrompt) => {
    const params = {
        fill: fillMode === 'ai' ? 'gen' : 'solid',
        fillColor: fillMode === 'transparent'
            ? '00ffffff'
            : String(fillColor || 'ffffff').replace('#', ''),
    };
    if (fillMode === 'ai' && fillPrompt) {
        params.fillGenPrompt = fillPrompt;
    }
    return params;
};

// Restore the editor's fill state (mode + colour + prompt) from stored crop params.
export const getFillStateFromParams = (params) => {
    const color = String(params.fillColor || '').replace('#', '').toLowerCase();
    // Alpha-first imgix hex with a 00 alpha (4- or 8-digit) ⇒ transparent fill.
    const transparent = (color.length === 8 && color.slice(0, 2) === '00')
        || (color.length === 4 && color[0] === '0');
    let fillMode = 'solid';
    if (params.fill === 'gen') fillMode = 'ai';
    else if (transparent) fillMode = 'transparent';
    let rgb = 'ffffff';
    if (color.length === 6) rgb = color;
    else if (color.length === 8) rgb = color.slice(2);
    else if (color.length === 3) rgb = color;
    else if (color.length === 4) rgb = color.slice(1).split('').map(c => c + c).join('');
    return { fillMode, fillColor: rgb, fillPrompt: params.fillGenPrompt || '' };
};

// Common aspect-ratio lock presets for the crop UI. Each non-square ratio is stored as a
// single magnitude (long:short ≥ 1); orientation (landscape vs portrait) is decided at
// apply time and toggled by re-clicking — so there are no cumbersome 16:9 / 9:16 duplicate
// buttons. `'original'` resolves to the image's own ratio at runtime; `null` is free-form.
export const aspectRatioPresets = [
    { key: 'free', label: 'Free', value: null },
    { key: 'original', label: 'Original', value: 'original' },
    { key: '1:1', label: '1:1', value: 1 },
    { key: '4:3', label: '4:3', value: 4 / 3 },
    { key: '3:2', label: '3:2', value: 3 / 2 },
    { key: '16:9', label: '16:9', value: 16 / 9 },
    // `'custom'` reveals a free-form "W:H" input; the ratio is parsed at runtime.
    { key: 'custom', label: 'Custom', value: 'custom' },
];
